<template>
  <div class="property-page">
    <div class="property-header-container">
      <h1 class="page-title">资产管理</h1>
      <div class="property-header">
        <div class="search-area">
          <a-input-search
            v-model:value="searchName"
            placeholder="按名称搜索"
            style="width: 200px; margin-right: 16px;"
            @search="handleSearch"
            allowClear
          />
          <a-select
            v-model:value="searchCategory"
            placeholder="按资产类别筛选"
            style="width: 160px; margin-right: 16px;"
            allowClear
            @change="handleSearch"
          >
            <a-select-option value="巡检设备">巡检设备</a-select-option>
            <a-select-option value="管理用房">管理用房</a-select-option>
          </a-select>
          <a-button @click="handleReset" style="margin-right: 16px;">重置</a-button>
        </div>
        <a-button type="primary" @click="handleAdd">新增资产</a-button>
      </div>
    </div>
    
    <div class="property-content">
      <a-table
        :dataSource="filteredPropertyList"
        :columns="columns"
        :pagination="{ pageSize: 8 }"
        :rowKey="record => record.id"
        :loading="loading"
        bordered
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'status'">
            <a-tag :color="getStatusColor(record.status)">
              {{ record.status }}
            </a-tag>
          </template>
          <template v-if="column.key === 'action'">
            <a-space>
              <a @click="handleEdit(record)">修改</a>
              <a-divider type="vertical" />
              <a-popconfirm
                title="确定要删除这条记录吗?"
                ok-text="是"
                cancel-text="否"
                @confirm="handleDelete(record)"
              >
                <a>删除</a>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </div>

    <!-- 新增/编辑表单弹窗 -->
    <a-modal
      v-model:visible="modalVisible"
      :title="modalTitle"
      @ok="handleModalOk"
      @cancel="handleModalCancel"
      :destroyOnClose="true"
    >
      <a-form
        :model="formState"
        :rules="rules"
        ref="formRef"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-item label="设备编号" name="id">
          <a-input v-model:value="formState.id" :disabled="modalType === 'edit'" />
        </a-form-item>
        <a-form-item label="名称" name="name">
          <a-input v-model:value="formState.name" />
        </a-form-item>
        <a-form-item label="资产类别" name="category">
          <a-select v-model:value="formState.category">
            <a-select-option value="巡检设备">巡检设备</a-select-option>
            <a-select-option value="管理用房">管理用房</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="经纬度" name="location">
          <a-input v-model:value="formState.location" placeholder="格式: 经度,纬度" />
        </a-form-item>
        <a-form-item label="备注" name="remarks">
          <a-textarea v-model:value="formState.remarks" :rows="4" />
        </a-form-item>
        <a-form-item label="状态" name="status">
          <a-select v-model:value="formState.status">
            <a-select-option value="正常">正常</a-select-option>
            <a-select-option value="损坏">损坏</a-select-option>
            <a-select-option value="维修">维修</a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted, watch } from 'vue';
import { message } from 'ant-design-vue';

export default defineComponent({
  name: 'PropertyList',
  setup() {
    const loading = ref(false);
    const modalVisible = ref(false);
    const modalType = ref('add');
    const formRef = ref();
    
    // 搜索条件
    const searchName = ref('');
    const searchCategory = ref(undefined);
    
    // 表格数据
    const propertyList = ref([
      {
        id: 'UAV-001',
        name: '无人机A型',
        category: '巡检设备',
        location: '121.602431,30.025134',
        remarks: '用于高空巡检',
        status: '正常'
      },
      {
        id: 'UAV-002',
        name: '无人机B型',
        category: '巡检设备',
        location: '121.603431,30.026134',
        remarks: '用于水域监测',
        status: '正常'
      },
      {
        id: 'UAV-003',
        name: '无人机C型',
        category: '巡检设备',
        location: '121.602831,30.025534',
        remarks: '用于热成像扫描',
        status: '维修'
      },
      {
        id: 'UAV-004',
        name: '无人机D型',
        category: '巡检设备',
        location: '121.604231,30.027134',
        remarks: '用于应急救援',
        status: '正常'
      },
      {
        id: 'UAV-005',
        name: '无人机E型',
        category: '巡检设备',
        location: '121.605431,30.028134',
        remarks: '用于大范围巡检',
        status: '正常'
      },
      {
        id: 'UAV-006',
        name: '无人机F型',
        category: '巡检设备',
        location: '121.606231,30.029534',
        remarks: '用于夜间巡检',
        status: '损坏'
      },
      {
        id: 'UAV-007',
        name: '无人机G型',
        category: '巡检设备',
        location: '121.607431,30.030134',
        remarks: '用于高精度测绘',
        status: '正常'
      },
      {
        id: 'CAR-001',
        name: '巡检车A型',
        category: '巡检设备',
        location: '121.608431,30.031134',
        remarks: '用于道路巡检',
        status: '正常'
      },
      {
        id: 'CAR-002',
        name: '巡检车B型',
        category: '巡检设备',
        location: '121.609231,30.032534',
        remarks: '用于设备运输',
        status: '维修'
      },
      {
        id: 'CAR-003',
        name: '巡检车C型',
        category: '巡检设备',
        location: '121.610431,30.033134',
        remarks: '用于应急处置',
        status: '正常'
      },
      {
        id: 'CAR-004',
        name: '巡检车D型',
        category: '巡检设备',
        location: '121.611431,30.034134',
        remarks: '用于夜间巡检',
        status: '正常'
      },
      {
        id: 'CAR-005',
        name: '巡检车E型',
        category: '巡检设备',
        location: '121.612231,30.035534',
        remarks: '用于工程抢修',
        status: '维修'
      },
      {
        id: 'CAR-006',
        name: '巡检车F型',
        category: '巡检设备',
        location: '121.613431,30.036134',
        remarks: '用于设备维护',
        status: '正常'
      },
      {
        id: 'CAR-007',
        name: '巡检车G型',
        category: '巡检设备',
        location: '121.614431,30.037134',
        remarks: '用于人员运输',
        status: '正常'
      },
      {
        id: 'CAR-008',
        name: '巡检车H型',
        category: '巡检设备',
        location: '121.615231,30.038534',
        remarks: '用于物资配送',
        status: '损坏'
      },
      {
        id: 'CAM-001',
        name: '摄像头A型',
        category: '巡检设备',
        location: '121.616431,30.039134',
        remarks: '用于入口监控',
        status: '正常'
      },
      {
        id: 'CAM-002',
        name: '摄像头B型',
        category: '巡检设备',
        location: '121.617431,30.040134',
        remarks: '用于出口监控',
        status: '正常'
      },
      {
        id: 'CAM-003',
        name: '摄像头C型',
        category: '巡检设备',
        location: '121.618231,30.041534',
        remarks: '用于区域监控',
        status: '正常'
      },
      {
        id: 'CAM-004',
        name: '摄像头D型',
        category: '巡检设备',
        location: '121.619431,30.042134',
        remarks: '用于设备监控',
        status: '维修'
      },
      {
        id: 'CAM-005',
        name: '摄像头E型',
        category: '巡检设备',
        location: '121.620431,30.043134',
        remarks: '用于人流监控',
        status: '正常'
      },
      {
        id: 'CAM-006',
        name: '摄像头F型',
        category: '巡检设备',
        location: '121.621231,30.044534',
        remarks: '用于车流监控',
        status: '正常'
      },
      {
        id: 'CAM-007',
        name: '摄像头G型',
        category: '巡检设备',
        location: '121.622431,30.045134',
        remarks: '用于夜视监控',
        status: '损坏'
      },
      {
        id: 'CAM-008',
        name: '摄像头H型',
        category: '巡检设备',
        location: '121.623431,30.046134',
        remarks: '用于热成像监控',
        status: '正常'
      },
      {
        id: 'CAM-009',
        name: '摄像头I型',
        category: '巡检设备',
        location: '121.624231,30.047534',
        remarks: '用于全景监控',
        status: '正常'
      },
      {
        id: 'CAM-010',
        name: '摄像头J型',
        category: '巡检设备',
        location: '121.625431,30.048134',
        remarks: '用于高空监控',
        status: '维修'
      },
      {
        id: 'HOUSE-001',
        name: '公园管理用房',
        category: '管理用房',
        location: '121.626431,30.049134',
        remarks: '用于公园设备管理',
        status: '正常'
      },
      {
        id: 'HOUSE-002',
        name: '社区用房',
        category: '管理用房',
        location: '121.627231,30.050534',
        remarks: '用于社区服务',
        status: '正常'
      },
      {
        id: 'HOUSE-003',
        name: '配套用房',
        category: '管理用房',
        location: '121.628431,30.051134',
        remarks: '用于设备存放',
        status: '正常'
      }
    ]);
    
    // 表单数据
    const formState = reactive({
      id: '',
      name: '',
      category: '巡检设备',
      location: '',
      remarks: '',
      status: '正常'
    });
    
    // 表单验证规则
    const rules = {
      id: [{ required: true, message: '请输入设备编号', trigger: 'blur' }],
      name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
      category: [{ required: true, message: '请选择资产类别', trigger: 'change' }],
      status: [{ required: true, message: '请选择状态', trigger: 'change' }]
    };
    
    // 表格列定义
    const columns = [
      {
        title: '设备编号',
        dataIndex: 'id',
        key: 'id',
        width: '100px'
      },
      {
        title: '名称',
        dataIndex: 'name',
        key: 'name',
        width: '150px'
      },
      {
        title: '资产类别',
        dataIndex: 'category',
        key: 'category',
        width: '100px'
      },
      {
        title: '经纬度',
        dataIndex: 'location',
        key: 'location',
        width: '150px'
      },
      {
        title: '备注',
        dataIndex: 'remarks',
        key: 'remarks',
        ellipsis: true
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: '100px'
      },
      {
        title: '操作',
        key: 'action',
        width: '150px',
        fixed: 'right'
      }
    ];
    
    // 计算属性：弹窗标题
    const modalTitle = computed(() => {
      return modalType.value === 'add' ? '新增资产' : '编辑资产';
    });
    
    // 根据状态获取标签颜色
    const getStatusColor = (status) => {
      switch (status) {
        case '正常':
          return 'green';
        case '损坏':
          return 'red';
        case '维修':
          return 'orange';
        default:
          return 'blue';
      }
    };
    
    // 计算属性：根据搜索条件过滤的资产列表
    const filteredPropertyList = computed(() => {
      return propertyList.value.filter(item => {
        // 名称筛选
        const nameMatch = searchName.value ? item.name.toLowerCase().includes(searchName.value.toLowerCase()) : true;
        
        // 资产类别筛选
        const categoryMatch = searchCategory.value ? item.category === searchCategory.value : true;
        
        return nameMatch && categoryMatch;
      });
    });
    
    // 处理新增按钮点击
    const handleAdd = () => {
      modalType.value = 'add';
      resetForm();
      modalVisible.value = true;
    };
    
    // 处理编辑按钮点击
    const handleEdit = (record) => {
      modalType.value = 'edit';
      Object.assign(formState, record);
      modalVisible.value = true;
    };
    
    // 处理删除按钮点击
    const handleDelete = (record) => {
      propertyList.value = propertyList.value.filter(item => item.id !== record.id);
      message.success('删除成功');
    };
    
    // 处理弹窗确认
    const handleModalOk = () => {
      formRef.value.validate().then(() => {
        if (modalType.value === 'add') {
          // 检查ID是否已存在
          const exists = propertyList.value.some(item => item.id === formState.id);
          if (exists) {
            message.error('设备编号已存在');
            return;
          }
          
          // 添加新记录
          propertyList.value.push({...formState});
          message.success('添加成功');
        } else {
          // 更新现有记录
          const index = propertyList.value.findIndex(item => item.id === formState.id);
          if (index !== -1) {
            propertyList.value[index] = {...formState};
            message.success('更新成功');
          }
        }
        
        modalVisible.value = false;
      }).catch(error => {
        console.log('验证失败:', error);
      });
    };
    
    // 处理弹窗取消
    const handleModalCancel = () => {
      modalVisible.value = false;
    };
    
    // 重置表单
    const resetForm = () => {
      formState.id = '';
      formState.name = '';
      formState.category = '巡检设备';
      formState.location = '';
      formState.remarks = '';
      formState.status = '正常';
      
      // 清除验证状态
      if (formRef.value) {
        formRef.value.resetFields();
      }
    };
    
    // 模拟加载数据
    const loadData = () => {
      loading.value = true;
      setTimeout(() => {
        loading.value = false;
      }, 500);
    };
    
    // 处理搜索
    const handleSearch = () => {
      console.log('搜索条件:', { name: searchName.value, category: searchCategory.value });
    };
    
    // 重置搜索条件
    const handleReset = () => {
      searchName.value = '';
      searchCategory.value = undefined;
    };

    // 页面加载时立即加载数据
    onMounted(() => {
      loadData();
    });
    
    return {
      loading,
      propertyList,
      filteredPropertyList,
      searchName,
      searchCategory,
      columns,
      modalVisible,
      modalTitle,
      modalType,
      formState,
      formRef,
      rules,
      getStatusColor,
      handleAdd,
      handleEdit,
      handleDelete,
      handleModalOk,
      handleModalCancel,
      handleSearch,
      handleReset
    };
  }
});
</script>

<style scoped>
.property-page {
  width: 100%;
  height: 100%;
  padding: 24px;
  background-color: #f0f2f5;
  display: flex;
  flex-direction: column;
}

.property-header-container {
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #1890ff;
  margin-bottom: 16px;
}

.property-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.search-area {
  display: flex;
  align-items: center;
  flex: 1;
}

.property-content {
  flex: 1;
  background-color: #fff;
  padding: 24px;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  overflow: auto;
}
</style>
